import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Box } from '@mui/material';
import { aiProcessRunsSignalRController } from 'app/services/signalR';
import {
  initAiProcessRunsConnection,
  teardownAiProcessRunsConnection,
} from 'app/services/signalR/aiProcessRunsConnector';
import PropTypes from 'prop-types';

import { AiProcessOwnerKind } from 'constants/aiProcessRuns';
import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadProcessesUsedByOwnerAction,
  selectExecutionAction,
  setRunsFilterAction,
} from 'store/aiProcessRuns';
import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';

import { containerStyles } from '../AiActivity.styles';
import { AiActivityTestIds } from '../AiActivity.testIds';
import { parseFiltersFromSearch, stringifyFiltersToSearch } from '../AiActivity.url';
import { normalizeExecutionId } from '../AiActivity.utils';
import ExecutionDetailDrawer from '../ExecutionDetailDrawer';
import LiveCard from '../LiveCard';
import OverviewCard from '../OverviewCard';
import RunHistoryCard from '../RunHistoryCard';
import StartAiProcessDrawer from '../StartAiProcessDrawer';

const AiActivityPanel = ({ ownerKind, ownerId, companyId, executionId, buildPath }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  const isExecuteAllowed = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.AI_PROCESSES_EXECUTE));
  // Starting a run means picking from GET /ai-processes, which needs AI Processes view access.
  const { isViewAllowed: isAiProcessesViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.AI_PROCESSES),
  );
  const isStartAiProcessAllowed = isExecuteAllowed && isAiProcessesViewAllowed;

  const [isStartDrawerOpen, setIsStartDrawerOpen] = useState(false);

  const selectedExecutionId = normalizeExecutionId(executionId);

  const filtersFromUrl = useMemo(() => parseFiltersFromSearch(search), [search]);

  const onSelectExecution = useCallback(
    executionId => {
      history.push({
        pathname: buildPath({ executionId }),
        search: stringifyFiltersToSearch(filtersFromUrl),
      });
    },
    [history, buildPath, filtersFromUrl],
  );

  const onCloseDrawer = useCallback(() => {
    history.push({
      pathname: buildPath(),
      search: stringifyFiltersToSearch(filtersFromUrl),
    });
  }, [history, buildPath, filtersFromUrl]);

  const onOpenStartDrawer = useCallback(() => {
    setIsStartDrawerOpen(true);
  }, []);

  const onCloseStartDrawer = useCallback(() => {
    setIsStartDrawerOpen(false);
  }, []);

  const onStartSuccess = useCallback(
    executionId => {
      setIsStartDrawerOpen(false);
      onSelectExecution(executionId);
    },
    [onSelectExecution],
  );

  const onOpenLiveRunFromPicker = useCallback(
    executionId => {
      setIsStartDrawerOpen(false);
      onSelectExecution(executionId);
    },
    [onSelectExecution],
  );

  const onFiltersChange = useCallback(
    partial => {
      const merged = { ...filtersFromUrl, ...partial };
      const nextSearch = stringifyFiltersToSearch(merged);

      history.push({
        pathname: buildPath({ executionId: selectedExecutionId }),
        search: nextSearch,
      });
    },
    [history, buildPath, filtersFromUrl, selectedExecutionId],
  );

  useEffect(() => {
    dispatch(setRunsFilterAction({ ownerId, partial: filtersFromUrl }));
  }, [dispatch, ownerId, filtersFromUrl]);

  useEffect(() => {
    dispatch(selectExecutionAction({ ownerId, executionId: selectedExecutionId }));
  }, [dispatch, ownerId, selectedExecutionId]);

  useEffectSafeStrictMode(() => {
    if (!ownerId) {
      return;
    }

    dispatch(loadProcessesUsedByOwnerAction({ ownerKind, ownerId }));
  }, [dispatch, ownerKind, ownerId]);

  useEffectSafeStrictMode(() => {
    const cancellation = { cancelled: false };

    initAiProcessRunsConnection(dispatch)
      .then(() => {
        if (cancellation.cancelled || !companyId || !ownerId) {
          return null;
        }

        return aiProcessRunsSignalRController.joinOwner(ownerKind, companyId, ownerId);
      })
      .catch(() => null);

    return () => {
      cancellation.cancelled = true;
      aiProcessRunsSignalRController
        .leaveOwner(ownerKind, companyId, ownerId)
        .catch(() => null)
        .finally(() => {
          teardownAiProcessRunsConnection().catch(() => null);
        });
    };
  }, [dispatch, ownerKind, companyId, ownerId]);

  return (
    <Box data-testid={AiActivityTestIds.CONTAINER} sx={containerStyles}>
      <OverviewCard
        isStartAllowed={isStartAiProcessAllowed}
        ownerId={ownerId}
        onStartAiProcessClick={onOpenStartDrawer}
      />
      <LiveCard ownerId={ownerId} onSelectExecution={onSelectExecution} />
      <RunHistoryCard
        filters={filtersFromUrl}
        isStartAllowed={isStartAiProcessAllowed}
        ownerId={ownerId}
        ownerKind={ownerKind}
        onFiltersChange={onFiltersChange}
        onSelectExecution={onSelectExecution}
        onStartAiProcessClick={onOpenStartDrawer}
      />
      <ExecutionDetailDrawer
        executionId={selectedExecutionId}
        ownerId={ownerId}
        onClose={onCloseDrawer}
      />
      <StartAiProcessDrawer
        open={isStartDrawerOpen}
        ownerId={ownerId}
        ownerKind={ownerKind}
        onClose={onCloseStartDrawer}
        onOpenLiveRun={onOpenLiveRunFromPicker}
        onStartSuccess={onStartSuccess}
      />
    </Box>
  );
};

AiActivityPanel.propTypes = {
  ownerKind: PropTypes.oneOf(Object.values(AiProcessOwnerKind)).isRequired,
  buildPath: PropTypes.func.isRequired,
  ownerId: PropTypes.string,
  companyId: PropTypes.string,
  executionId: PropTypes.string,
};

export default memo(AiActivityPanel);
